'use strict';

var framerMotion = require('framer-motion');
var React = require('react');
var reactIcons = require('@sravni/react-icons');
var reactUtils = require('@sravni/react-utils');
var zIndexes = require('../../../constants/zIndexes.js');
var Icon = require('../../Icon/src/Icon.js');
var Portal = require('../../Portal/src/Portal.js');
var index = require('../../Sheet/index.js');
var Space = require('../../Space/src/Space.js');
var _utils = require('../../Transition/src/_utils.js');
var Fade = require('../../Transition/src/Fade.js');
require('../../Transition/src/ScaleFade.js');
require('../../Transition/src/SlideFade.js');
require('../../Transition/src/Collapse.js');
var Popover_module = require('../styles/Popover.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

const backdropMotionProps = (visible) => ({
    variants: {
        exit: {
            opacity: 0,
            transition: {
                duration: 0.15,
                ease: _utils.EASINGS.easeOut,
            },
        },
        enter: {
            opacity: 0.5,
            transition: {
                duration: 0.2,
                ease: _utils.EASINGS.easeIn,
            },
        },
    },
    animate: visible ? 'enter' : 'exit',
    initial: 'exit',
    exit: 'exit',
});
const popoverMotionProps = (visible) => ({
    ...Fade.fadeConfig,
    animate: visible ? 'enter' : 'exit',
});
const defaultPositionerSettings = {
    matchWidth: false,
    placement: 'bottom-start',
    flip: true,
    offset: [0, 4],
};
/**
 * @description The component can show arbitrary content in the popup window (desktop mode) or sheet (mobile mode).
 *
 * @component
 * @example
 * ```jsx
 * <Popover content={<Component/>}>triggerElement</Popover>
 * ```
 */
const Popover = React__namespace.forwardRef((props, ref) => {
    const { visible, header, content, footer, backdrop, mobile, closable, fullscreen, positionerSettings, className, children, onClose, ...restProps } = props;
    const reference = React__namespace.useRef(null);
    const popper = React__namespace.useRef(null);
    const { referenceRef, getPopperProps } = reactUtils.usePositioner({
        ...defaultPositionerSettings,
        ...positionerSettings,
    });
    const cx = reactUtils.useStyles(Popover_module.default);
    const containerClassNames = cx(className, 'container');
    let triggerElement = null;
    const popoverVisible = !!visible && (!!header || !!content || !!footer);
    const withBackdrop = popoverVisible && !!backdrop;
    // Ensure Popover has only one child node
    const count = React__namespace.useMemo(() => React__namespace.Children.count(children), [children]);
    const condition = count === 0 || count > 1;
    if (condition) {
        reactUtils.error({
            condition,
            message: 'Ensure Popover has only one child node',
        })();
        triggerElement = children;
    }
    else {
        const child = React__namespace.Children.only(children);
        const triggerClassNames = cx(child.props.className, {
            triggerBackdrop: withBackdrop,
        });
        const desktopProps = {
            ...child.props,
            className: triggerClassNames,
            // eslint-disable-next-line react-hooks/rules-of-hooks
            ref: reactUtils.useMergedRef(child.ref, reference, referenceRef),
        };
        triggerElement = mobile ? React__namespace.cloneElement(child, child.props) : React__namespace.cloneElement(child, desktopProps);
    }
    const desktopView = React__namespace.useMemo(() => (React__namespace.createElement(Portal.Portal, null,
        React__namespace.createElement(framerMotion.AnimatePresence, null, popoverVisible && (React__namespace.createElement("div", { className: cx({ overlay: withBackdrop }) },
            withBackdrop && React__namespace.createElement(framerMotion.motion.div, { ...backdropMotionProps(popoverVisible), className: cx('backdrop') }),
            React__namespace.createElement(framerMotion.motion.div, { ...popoverMotionProps(popoverVisible), ...getPopperProps({ style: { zIndex: zIndexes.Z_INDEXES.DROPDOWN } }, popper) },
                React__namespace.createElement(Space.Space, { ref: ref, "data-qa": "Popover", direction: "vertical", size: 16, className: containerClassNames, ...restProps },
                    closable && React__namespace.createElement(Icon.Icon, { className: cx('close'), size: 24, icon: React__namespace.createElement(reactIcons.Cross, null), onClick: onClose }),
                    header && React__namespace.createElement("div", { className: cx('header') }, header),
                    content && React__namespace.createElement("div", { className: cx('content') }, content),
                    footer && React__namespace.createElement("div", null, footer)))))))), [
        popoverVisible,
        cx,
        withBackdrop,
        getPopperProps,
        ref,
        containerClassNames,
        restProps,
        closable,
        onClose,
        header,
        content,
        footer,
    ]);
    const mobileView = React__namespace.useMemo(() => (React__namespace.createElement(index.Sheet, { ref: ref, "data-qa": "Popover", className: className, visible: popoverVisible, fullscreen: fullscreen, closable: closable, onClose: onClose, ...restProps },
        header && React__namespace.createElement(index.Sheet.Header, null, header),
        content && React__namespace.createElement(index.Sheet.Content, null, content),
        footer && React__namespace.createElement(index.Sheet.Footer, null, footer))), [ref, popoverVisible, header, content, footer, closable, fullscreen, className, restProps, onClose]);
    const view = React__namespace.useMemo(() => (mobile ? mobileView : desktopView), [mobile, desktopView, mobileView]);
    reactUtils.useOutsideClick([reference, popper], () => onClose?.(), popoverVisible);
    reactUtils.useOutsideClick(reference, () => onClose?.(), !popoverVisible);
    reactUtils.useKeyPress({
        enabled: popoverVisible,
        keyMap: {
            Escape: (event) => {
                event.preventDefault();
                onClose?.();
            },
            Tab: () => {
                onClose?.();
            },
        },
    }, [popoverVisible, onClose, onClose]);
    return (React__namespace.createElement(React__namespace.Fragment, null,
        triggerElement,
        view));
});
Popover.defaultProps = {
    positionerSettings: defaultPositionerSettings,
};
Popover.displayName = 'Popover';

exports.Popover = Popover;
//# sourceMappingURL=Popover.js.map
